import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const SEVERITY_COLORS = {
  LOW:      '#66bb6a',
  MODERATE: '#ffa726',
  HIGH:     '#ef5350',
  EXTREME:  '#b71c1c',
  UNKNOWN:  '#546e7a',
};

export default function SeverityChart({ eventsBySeverity, loading }) {
  const labels  = Object.keys(SEVERITY_COLORS);
  const values  = labels.map(k => eventsBySeverity?.[k] || 0);
  const total   = values.reduce((a, b) => a + b, 0);

  const data = {
    labels,
    datasets: [{
      data: values,
      backgroundColor: labels.map(k => `${SEVERITY_COLORS[k]}cc`),
      borderColor:     labels.map(k => SEVERITY_COLORS[k]),
      borderWidth: 1.5,
      hoverOffset: 6,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#90caf9',
          font: { size: 11, weight: '600' },
          boxWidth: 10,
          padding: 12,
          generateLabels: (chart) => {
            const ds = chart.data.datasets[0];
            return chart.data.labels.map((label, i) => ({
              text: `${label}  ${ds.data[i]}`,
              fillStyle: ds.backgroundColor[i],
              strokeStyle: ds.borderColor[i],
              hidden: ds.data[i] === 0,
              index: i,
            }));
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(10,22,40,0.95)',
        borderColor: 'rgba(255,255,255,0.3)', borderWidth: 1,
        titleColor: '#e3f2fd', bodyColor: '#90caf9',
        padding: 10, cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.parsed} events (${total ? ((ctx.parsed / total) * 100).toFixed(1) : 0}%)`,
        },
      },
    },
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
          Severity Distribution
        </Typography>
        {loading ? (
          <Skeleton variant="circular" width={160} height={160} sx={{ mx: 'auto', bgcolor: 'rgba(255,255,255,0.05)' }} />
        ) : total === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No data yet</Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 200, position: 'relative' }}>
            <Doughnut data={data} options={options} />
            {/* Center label */}
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.light', lineHeight: 1 }}>{total}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total</Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
